if (!customElements.get('product-form')) {
  customElements.define('product-form', class ProductForm extends HTMLElement {
    constructor() {
      super();
      this.initialised = false;
      // Bound once so connectedCallback and disconnectedCallback add and
      // remove the SAME listener reference.
      this.init = this.init.bind(this);
    }

    /**
     * ⛔ WHY THE WIRING IS NOT IN THE CONSTRUCTOR, AND WHY connectedCallback
     * ALONE IS NOT ENOUGH EITHER. Both were measured on the live storefront,
     * 2026-09-25, on /products/62x90-cover-panel-plain.
     *
     * This element used to wire itself up in its constructor. A custom
     * element constructor runs the moment the element is UPGRADED, and for
     * an element in the page's own HTML that is while the parser is still
     * inside it, so `this.querySelector('form')` returns null and the
     * children do not exist yet. The old code dereferenced that null and
     * threw "Cannot set properties of null (setting 'disabled')".
     *
     * It was intermittent, and the reason is an APP, not the theme. The
     * bcpo app injects this very script itself:
     *
     *     var script = document.createElement('script');
     *     script.defer = true;
     *     script.src = "…/assets/product-form.js?v=…";
     *     document.head.append(script);
     *
     * `defer` has NO EFFECT on a dynamically inserted script, so the file
     * runs as soon as it downloads, racing the <product-form> element that
     * sits ~556,000 bytes into the page. Win the race and the element
     * upgrades with its children present. Lose it and it does not.
     *
     * ⚠ AND connectedCallback FIRES ON THE OPENING TAG. Moving the wiring
     * there alone would change nothing: the parser calls it as soon as it
     * sees `<product-form>`, still before the children. So connection is
     * only the TRIGGER here, and the actual wiring waits for the document
     * to finish parsing when it has not finished yet.
     *
     * ⛔ WHAT THE RACE COST, so nobody "simplifies" this back. When it was
     * lost, this element never bound its submit handler, so Add to Cart
     * fell through to a native form POST: the item was still added, but the
     * customer got a full page navigation to /cart instead of the cart
     * drawer opening. No order was ever lost. The guards below are kept as
     * well, because an app also injects a BARE <product-form> with no form
     * and no [name=id] at all, and a missing node must mean "skip that
     * wiring", never a throw.
     */
    connectedCallback() {
      if (this.initialised) return;
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', this.init, { once: true });
      } else {
        // Parsed already, or injected by an app after load: children are
        // there now, so wire immediately rather than waiting for an event
        // that has already fired.
        this.init();
      }
    }

    disconnectedCallback() {
      // An element removed before the document finished parsing must not
      // leave a listener behind holding it alive.
      document.removeEventListener('DOMContentLoaded', this.init);
    }

    init() {
      if (this.initialised) return;
      this.initialised = true;

      this.form = this.querySelector('form');
      if (this.form) {
        const idInput = this.form.querySelector('[name=id]');
        if (idInput) idInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
      } else {
        const idInput = this.querySelector('[name=id]');
        if (idInput) idInput.disabled = false;
        const submit = this.querySelector('button[type=submit]');
        if (submit) submit.addEventListener('click', this.onSubmitHandler.bind(this));
      }

      this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      this.submitButton = this.querySelector('[type="submit"]');
      if (document.querySelector('cart-drawer') && this.submitButton) {
        this.submitButton.setAttribute('aria-haspopup', 'dialog');
      }
      this.hideErrors = this.dataset.hideErrors === 'true';
    }

    onSubmitHandler(evt) {
      evt.preventDefault();
      // Re-lookup cart in case DOM changed since init
      this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      // Guarded for the same reason as init: without a submit button there
      // is nothing to disable and nothing to submit.
      if (!this.submitButton) return;
      if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

      this.handleErrorMessage();

      this.submitButton.setAttribute('aria-disabled', true);
      this.submitButton.classList.add('loading');
      const spinner = this.querySelector('.loading-overlay__spinner');
      if (spinner) spinner.classList.remove('hidden');

      const config = fetchConfig('javascript');
      config.headers['X-Requested-With'] = 'XMLHttpRequest';
      delete config.headers['Content-Type'];

      const formData = new FormData(this.form);
      if (!this.form) {
        const idInput = this.querySelector('[name=id]');
        if (idInput) formData.append('id', idInput.value);
      }

      if (this.cart) {
        formData.append('sections', this.cart.getSectionsToRender().map((section) => section.id));
        formData.append('sections_url', window.location.pathname);
        this.cart.setActiveElement(document.activeElement);
      }
      config.body = formData;

      fetch(`${routes.cart_add_url}`, config)
        .then((response) => response.json())
        .then((response) => {
          if (response.status) {
            publish(PUB_SUB_EVENTS.cartError, {source: 'product-form', productVariantId: formData.get('id'), errors: response.description, message: response.message});
            this.handleErrorMessage(response.description);
            const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
            if (!soldOutMessage) return;
            this.submitButton.setAttribute('aria-disabled', true);
            this.submitButton.querySelector('span').classList.add('hidden');
            soldOutMessage.classList.remove('hidden');
            this.error = true;
            return;
          } else if (!this.cart) {
            window.location = window.routes.cart_url;
            return;
          }

          if (!this.error) publish(PUB_SUB_EVENTS.cartUpdate, {source: 'product-form', productVariantId: formData.get('id')});
          this.error = false;
          const quickAddModal = this.closest('quick-add-modal');
          if (quickAddModal) {
            document.body.addEventListener('modalClosed', () => {
              setTimeout(() => { this.cart.renderContents(response) });
            }, { once: true });
            quickAddModal.hide(true);
          } else {
            this.cart.renderContents(response);
            if (window.easifyHandleCalculateCart && typeof window.easifyHandleCalculateCart === 'function') {
              window.easifyHandleCalculateCart();
            }
          }
        })
        .catch((e) => {
          console.error(e);
        })
        .finally(() => {
          this.submitButton.classList.remove('loading');
          if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
          if (!this.error) this.submitButton.removeAttribute('aria-disabled');
          const spinnerDone = this.querySelector('.loading-overlay__spinner');
          if (spinnerDone) spinnerDone.classList.add('hidden');
        });
    }

    handleErrorMessage(errorMessage = false) {
      if (this.hideErrors) return;

      this.errorMessageWrapper = this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
      if (!this.errorMessageWrapper) return;
      this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

      this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

      if (errorMessage) {
        this.errorMessage.textContent = errorMessage;
      }
    }
  });
}
